import React, { useRef, cloneElement, isValidElement, forwardRef } from 'react'
import PropTypes from 'prop-types'
import { createUseStyles } from 'react-jss'
import ModalBase from 'react-overlays/Modal'
import FocusTrap from 'focus-trap-react'
import clsx from 'clsx'
import Fade from '../Fade'
import { cssSelector } from '../../utils/classes'
import withTheme from '../styles/withTheme'

const styles = {
	modal: {
		position: 'fixed',
		zIndex: 13000,
		right: 0,
		bottom: 0,
		top: 0,
		left: 0
	},
	modalBackdrop: {
		zIndex: 13000,
		position: 'fixed',
		right: '-100%',
		bottom: '-100%',
		top: '-100%',
		left: '-100%',
		backgroundColor: (props) => (props.theme === 'dark' ? 'rgba(255, 255, 255, .6)' : 'rgba(0, 0, 0, .6)'),
		opacity: 0,
		'-webkit-tap-highlight-color': 'transparent',
		transition: (props) => `opacity ${props.transitionDuration}ms ease-in-out`,
		'@media (max-width: 575px)': {
			transition: 'none',
			backgroundColor: (props) => (props.theme === 'dark' ? '#000' : '#fff'),
		}
	},
	modalBackdropIn: {
		opacity: '1 !important'
	},
	modalDialogContainer: {
		position: 'fixed',
		top: 0,
		left: 0,
		bottom: 0,
		right: 0,
		display: 'flex',
		alignItems: (props) => (props.verticalPosition === 'center' ? 'center' : 'flex-start'),
		'@media (max-height: 29.9999rem)': {
			maxHeight: '100%',
			overflow: 'auto'
		},
	},
	modalDialog: {
		display: 'flex',
		flexDirection: 'column',
		maxWidth: '100%',
		position: 'relative'
	},
	modalDialogDefault: {
		extend: 'modalDialog',
		width: '36rem',
		margin: '1.75rem auto',
		backgroundColor: (props) => (props.theme === 'dark' ? '#424b5a' : 'white'),
		'@media (min-height: 30rem)': {
			maxHeight: 'calc(100% - 3.5rem)'
		},
		'@media (max-width: 575px)': {
			width: '100%',
			margin: '0 auto'
		},
		'@media (max-width: 575px) and (min-height: 30rem)': {
			height: '100%',
			maxHeight: 'none'
		}
	},
	modalDialogfullWidth: {
		extend: 'modalDialog',
		width: '100%',
		margin: '0 auto',
		backgroundColor: (props) => (props.theme === 'dark' ? '#424b5a' : 'white'),
		minHeight: '100%',
		'@media (min-height: 30rem)': {
			height: '100%'
		}
	},
	modalDialogLargeWidth: {
		extend: 'modalDialog',
		width: '95%',
		margin: '1.75rem auto',
		backgroundColor: (props) => (props.theme === 'dark' ? '#424b5a' : 'white'),
		'@media (min-height: 30rem)': {
			maxHeight: 'calc(100% - 3.5rem)'
		},
		'@media (max-width: 575px)': {
			width: '100%',
			margin: '0 auto'
		},
		'@media (max-width: 575px) and (min-height: 30rem)': {
			height: '100%',
			maxHeight: 'none'
		}
	}
}

const useStyles = createUseStyles(styles, { name: 'Vb' })

const Modal = forwardRef(function Modal(props, ref) {
	const {
		open,
		children,
		className,
		onClose,
		disableBackdropClick,
		disableEscapeKeyDown,
		ModalDialogProps,
		TransitionComponent = Fade,
		transitionDuration = 150,
		fullWidth = false,
		size = 'medium',
		verticalPosition = 'top'
	} = props

	const modalDialogRef = useRef()

	const classes = useStyles(props)

	function handleEscapeKeyDown(e) {
		e.preventDefault()
		if (!disableEscapeKeyDown) {
			handleClose()
		}
	}

	function handleClose() {
		onClose()
	}

	function handleModalClick(e) {
		if (disableBackdropClick) {
			return
		}
		if (!modalDialogRef.current.contains(e.target)) {
			handleClose()
		}
	}
	function widthSize(size) {
		if (fullWidth) {
			return classes.modalDialogfullWidth
		}

		switch (size) {
			case 'fullWidth':
				return classes.modalDialogfullWidth
			case 'large':
				return classes.modalDialogLargeWidth
			case 'medium':
			default:
				return classes.modalDialogDefault
		}
	}

	const ModalBackdrop = (
		<TransitionComponent in={open} timeout={0} zIndex={1300}>
			<div className={clsx(classes.modalBackdrop, open && classes.modalBackdropIn)} aria-hidden />
		</TransitionComponent>
	)

	return (
		<ModalBase
			ref={ref}
			show={open}
			className={clsx(classes.modal, className)}
			onHide={handleClose}
			onClick={handleModalClick}
			renderBackdrop={() => (!fullWidth ? ModalBackdrop : null)}
			onEscapeKeyDown={handleEscapeKeyDown}
		>
			<TransitionComponent in={open} timeout={transitionDuration}>
				<FocusTrap
					focusTrapOptions={{
						fallbackFocus: cssSelector(classes.modal),
						initialFocus: cssSelector(classes.modal),
						allowOutsideClick: true,
						preventScroll: true
					}}
				>
					<div className={classes.modalDialogContainer}>
						<div ref={modalDialogRef} className={clsx(widthSize(size))} {...ModalDialogProps}>
							{React.Children.map(children, (child) => {
								if (isValidElement(child)) {
									if (child.type.name === 'ModalHeader') {
										return cloneElement(child, { defaultOnClose: handleClose })
									} else {
										return cloneElement(child)
									}
								}
								return child
							})}
						</div>
					</div>
				</FocusTrap>
			</TransitionComponent>
		</ModalBase>
	)
})

export default withTheme('Vb-modal')(Modal)

Modal.propTypes = {
	/**
	 * Modal children, usually the included sub-components.
	 */
	children: PropTypes.node,
	/**
	 * @ignore
	 */
	className: PropTypes.string,
	/**
	 * If `true`, clicking the backdrop will not fire the `onClose` callback.
	 * @default false
	 */
	disableBackdropClick: PropTypes.bool,
	/**
	 * If `true`, hitting escape will not fire the `onClose` callback.
	 * @default false
	 */
	disableEscapeKeyDown: PropTypes.bool,
	/**
	 * Sets the size of the modal. One of:
	 * - `medium` (default)
	 * - `large` (95%)
	 * - `fullWidth` (100%)
	 *
	 * Notice that the dialog width grow is limited by the default margin.
	 * @default 'medium'
	 */
	size: PropTypes.oneOf(['medium', 'large', 'fullWidth']),
	/**
	 * Callback fired when the component requests to be closed.
	 *
	 * @param {object} event The event source of the callback.
	 */
	onClose: PropTypes.func,
	/**
	 * If `true`, the Dialog is open.
	 */
	open: PropTypes.bool,
	/**
	 * The component used for the transition.
	 * [Follow this guide](/components/transitions/#transitioncomponent-prop) to learn more about the requirements for this component.
	 * @default Fade
	 */
	TransitionComponent: PropTypes.elementType,
	/**
	 * The duration for the transition, in milliseconds.
	 * You may specify a single timeout for all transitions, or individually with an object.
	 * @default { enter: duration.enteringScreen, exit: duration.leavingScreen }
	 */
	transitionDuration: PropTypes.oneOfType([
		PropTypes.number,
		PropTypes.shape({
			appear: PropTypes.number,
			enter: PropTypes.number,
			exit: PropTypes.number
		})
	]),
	/**
	 * **Deprecated:** Use `size` prop instead
	 * @default false
	 */
	fullWidth: PropTypes.bool,
	/**
	 * Vertical position of the modal.
	 * @default 'top'
	 */
	verticalPosition: PropTypes.oneOf(['top', 'center']),
}
